export type StandardCursor =
  | 'default'
  | 'pointer'
  | 'move'
  | 'text'
  | 'crosshair'
  | 'not-allowed'
  | 'grab'
  | 'grabbing'
  | 'nwse-resize'
  | 'nesw-resize'
  | 'ew-resize'
  | 'ns-resize'
  | 'n-resize'
  | 's-resize'
  | 'w-resize'
  | 'e-resize'
  | 'ne-resize'
  | 'se-resize'
  | 'sw-resize'
  | 'nw-resize'
  | 'zoom-in'
  | 'zoom-out'
  | 'help'
  | 'wait'
  | 'progress'
  | 'copy'
  | 'alias'
  | 'context-menu'
  | 'cell'
  | 'vertical-text'
  | 'no-drop'
  | 'not-allowed'
  | 'all-scroll'
  | 'col-resize'
  | 'row-resize'
  | 'none'
  | 'inherit'
  | 'initial'
  | 'unset';

export type URLCursor = `url(${string})`;

export type URLCursorWithCoords = `url(${string}) ${number} ${number}`;

export type URLCursorWithFallback =
  | `${URLCursor}, ${StandardCursor}`
  | `${URLCursorWithCoords}, ${StandardCursor}`;

export type CursorType =
  | StandardCursor
  | URLCursor
  | URLCursorWithCoords
  | URLCursorWithFallback;

// Explicit light and dark contours survive theme changes and native cursor
// switches in Chromium. Keep these shared by Edgeless and DOM projections.
function outlinedCursor(
  path: string,
  fallback: StandardCursor,
  x: number,
  y: number
): URLCursorWithFallback {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="${path}" fill="white" stroke="white" stroke-width="4" stroke-linejoin="round"/><path d="${path}" fill="white" stroke="#171717" stroke-width="1.5" stroke-linejoin="round"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${x} ${y}, ${fallback}`;
}

const selectionCursor = outlinedCursor(
  'M5 3L5 24L10 19L14 28L18 26L14 17L22 17Z',
  'default',
  5,
  3
);
const panCursor = outlinedCursor(
  'M10 16V8Q10 5 12 5Q14 5 14 8V14V5Q14 2 16 2Q18 2 18 5V14V7Q18 4 20 4Q22 4 22 7V15V11Q22 8 24 8Q26 8 26 11V20Q26 26 22 29H14L5 19Q3 16 5 15Q7 14 10 18Z',
  'grab',
  16,
  16
);
const panningCursor = outlinedCursor(
  'M9 15V12Q9 9 12 9Q14 9 14 12V10Q14 7 17 7Q19 7 19 10V12Q19 9 22 9Q24 9 24 12V14Q24 12 26 12Q28 12 28 15V21Q28 26 24 29H13L6 22Q3 19 5 17Q7 15 10 19Z',
  'grabbing',
  16,
  16
);
export const canvasPointerCursor = outlinedCursor(
  'M12 17V5Q12 2 14 2Q16 2 16 5V13Q18 11 20 14Q22 12 24 15Q28 13 28 18V22Q28 26 24 29H15L6 20Q4 17 6 16Q8 15 12 19Z',
  'pointer',
  14,
  3
);

/** Shared navigation feedback for Edgeless and retained DOM projections. */
export function navigationCursor(
  mode: 'select' | 'pan',
  dragging = false
): URLCursorWithFallback {
  return mode === 'select'
    ? selectionCursor
    : dragging
      ? panningCursor
      : panCursor;
}
